import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Settings, Code, Search, Image, Video,
  Shield, Mail, Link2, FileText, Plus, Trash2, X,
  ExternalLink, Upload, Check, AlertCircle, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Site settings that belong here — domains live in Publish; theme colors/fonts in editor Design.
const TABS = [
  { id: 'general', name: 'General', icon: Settings },
  { id: 'seo', name: 'SEO', icon: Search },
  { id: 'custom-code', name: 'Custom Code', icon: Code },
  { id: 'media', name: 'File Manager', icon: Image },
  { id: 'privacy', name: 'Cookies & Privacy', icon: Shield },
  { id: 'integrations', name: 'Integrations', icon: Link2 },
  { id: 'auth', name: 'Visitor Auth', icon: Users },
  { id: 'email', name: 'Emails', icon: Mail },
  { id: 'redirects', name: 'URL Redirects', icon: FileText },
];

const WebsiteSettings = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [website, setWebsite] = useState(null);
  
  // Settings state
  const BOT_HELP_TEXT = 'Connect an existing AI Bot to power the chat widget on all public pages for this website.';

  const [settings, setSettings] = useState({
    name: '',
    description: '',
    logo_url: '',
    favicon_url: '',
    og_image_url: '',
    seo_title: '',
    seo_description: '',
    seo_keywords: '',
    primary_color: '#8b5cf6',
    secondary_color: '#10b981',
    font_family: 'Inter',
    custom_head_code: '',
    custom_body_code: '',
    custom_css: '',
    custom_domain: '',
    ssl_enabled: true,
    timezone: 'UTC',
    date_format: 'MM/DD/YYYY',
    time_format: '12h',
    languages: ['en'],
    cookie_consent_enabled: true,
    cookie_consent_message: 'We use cookies to improve your experience.',
    privacy_policy_url: '',
    terms_url: '',
    social_facebook: '',
    social_twitter: '',
    social_instagram: '',
    social_linkedin: '',
    social_youtube: '',
    google_analytics_id: '',
    facebook_pixel_id: '',
    contact_email: '',
    from_email: '',
    email_footer: '',
    chatbot_bot_id: ''
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  
  // Redirects state
  const [redirects, setRedirects] = useState([]);
  const [newRedirect, setNewRedirect] = useState({ from_path: '', to_path: '', redirect_type: '301' });
  
  // Media state
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaFilter, setMediaFilter] = useState('all');
  const [newMediaUrl, setNewMediaUrl] = useState('');
  const [newMediaName, setNewMediaName] = useState('');

  // Bots for website chatbot
  const [bots, setBots] = useState([]);
  const [botsLoading, setBotsLoading] = useState(false);
  const [genreAgents, setGenreAgents] = useState(null);
  const [creatingGenreBot, setCreatingGenreBot] = useState(false);

  const [paymentSettings, setPaymentSettings] = useState({
    stripe_enabled: false,
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    razorpay_enabled: false,
    razorpay_key_id: '',
    razorpay_key_secret: '',
    razorpay_webhook_secret: '',
    paypal_enabled: false,
    paypal_client_id: '',
    paypal_client_secret: '',
    default_currency: 'USD',
    test_mode: true,
  });
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsSaving, setPaymentsSaving] = useState(false);

  const [integrationStatus, setIntegrationStatus] = useState({
    emailConfigured: false,
    smsConfigured: false,
    fromEmail: '',
    emailProvider: '',
    smsProvider: '',
    inquiryNotifyOwner: true,
    leadNotifyOwner: true,
  });
  const [integrationsLoading, setIntegrationsLoading] = useState(false);

  const [siteUsers, setSiteUsers] = useState([]);
  const [siteUsersLoading, setSiteUsersLoading] = useState(false);
  const [deletingSiteUserId, setDeletingSiteUserId] = useState(null);

  const token = localStorage.getItem('token');
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
  }, [websiteId]);

  useEffect(() => {
    if (activeTab === 'integrations') {
      fetchBots();
      fetchPaymentSettings();
      fetchIntegrationStatus();
    }
    if (activeTab === 'auth') {
      fetchSiteUsers();
    }
    if (activeTab === 'media') {
      fetchMediaFiles();
    }
  }, [activeTab, websiteId]);

  const fetchPaymentSettings = async () => {
    if (!websiteId || !token) return;
    setPaymentsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/${websiteId}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPaymentSettings((prev) => ({ ...prev, ...data }));
      }
    } catch (e) {
      console.error('Failed to load payment settings', e);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const savePaymentSettings = async () => {
    if (!websiteId || !token) return;
    setPaymentsSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/payments/${websiteId}/settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentSettings),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      if (data.settings) setPaymentSettings((prev) => ({ ...prev, ...data.settings }));
      toast.success('Payment gateway settings saved');
    } catch (e) {
      toast.error(e.message || 'Could not save payment settings');
    } finally {
      setPaymentsSaving(false);
    }
  };

  const updatePaymentSetting = (field, value) => {
    setPaymentSettings((prev) => ({ ...prev, [field]: value }));
  };

  const fetchIntegrationStatus = async () => {
    if (!websiteId || !token) return;
    setIntegrationsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/operator-integrations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const email = data.email || {};
      const sms = data.sms || {};
      const notes = data.notifications || {};
      const emailConfigured = !!(email.from_email && (
        email.provider === 'smtp' ? email.smtp_host : email.sendgrid_api_key_set || email.mailgun_api_key_set || email.ses_access_key_id
      ));
      const smsConfigured = !!(sms.twilio_account_sid && (sms.twilio_auth_token_set || sms.textlocal_api_key_set));
      setIntegrationStatus({
        emailConfigured,
        smsConfigured,
        fromEmail: email.from_email || '',
        emailProvider: email.provider || 'smtp',
        smsProvider: sms.provider || 'twilio',
        inquiryNotifyOwner: notes.inquiry_notify_owner !== false,
        leadNotifyOwner: notes.lead_notify_owner !== false,
      });
    } catch (e) {
      console.error('Failed to load integration status', e);
    } finally {
      setIntegrationsLoading(false);
    }
  };

  const publishedSiteBase = website?.slug
    ? `${API_URL}/api/sites/${website.slug}/html`
    : `${API_URL}/api/sites/${websiteId}/html`;

  const fetchSiteUsers = async () => {
    if (!websiteId || !token) return;
    setSiteUsersLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/site-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setSiteUsers(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Failed to load site users', e);
    } finally {
      setSiteUsersLoading(false);
    }
  };

  const deleteSiteUser = async (siteUserId) => {
    if (!websiteId || !token || !siteUserId) return;
    if (!window.confirm('Remove this visitor account? They will need to register again to sign in.')) return;
    setDeletingSiteUserId(siteUserId);
    try {
      const res = await fetch(`${API_URL}/api/websites/${websiteId}/site-users/${siteUserId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Delete failed');
      }
      setSiteUsers((prev) => prev.filter((u) => u.id !== siteUserId));
      toast.success('Visitor account removed');
    } catch (e) {
      toast.error(e.message || 'Could not remove visitor');
    } finally {
      setDeletingSiteUserId(null);
    }
  };

  const fetchBots = async () => {
    try {
      setBotsLoading(true);
      const res = await fetch(`${API_URL}/api/my/bots`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setBots(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load bots', error);
    } finally {
      setBotsLoading(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, [websiteId]);

  useEffect(() => {
    const category = website?.category;
    if (!category) {
      setGenreAgents(null);
      return;
    }
    fetch(`${API_URL}/api/ai-studio/templates/for-website-category/${encodeURIComponent(category)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setGenreAgents(data))
      .catch(() => setGenreAgents(null));
  }, [website?.category]);

  const createGenreChatbot = async () => {
    const primary = (genreAgents?.templates || []).find((t) => t.is_primary) || genreAgents?.templates?.[0];
    if (!primary?.id) return;
    setCreatingGenreBot(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-studio/apps/from-agent-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          template_id: primary.id,
          website_id: websiteId,
          website_name: settings.name || website?.name,
          link_website_chatbot: true,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to create chatbot');
      if (data.bot_id) {
        updateSetting('chatbot_bot_id', data.bot_id);
        setSettings((prev) => ({ ...prev, chatbot_bot_id: data.bot_id }));
        await fetch(`${API_URL}/api/websites/${websiteId}/settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ chatbot_bot_id: data.bot_id }),
        });
      }
      await fetchBots();
      toast.success(`${data.agent?.name || primary.name} created and linked to this site.`);
    } catch (err) {
      toast.error(err.message || 'Could not create industry chatbot');
    } finally {
      setCreatingGenreBot(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        toast.error(typeof err.detail === 'string' ? err.detail : 'Failed to load settings');
        setSettingsLoaded(false);
        setLoading(false);
        return;
      }

      const data = await response.json();
      setWebsite(data);
      setSettings(prev => ({
        ...prev,
        ...data,
        logo_url: data.logo_url || '',
        favicon_url: data.favicon_url || '',
        og_image_url: data.og_image_url || '',
      }));
      setSettingsLoaded(true);
      
      // Fetch redirects
      const redirectsRes = await fetch(`${API_URL}/api/websites/${websiteId}/redirects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (redirectsRes.ok) {
        setRedirects(await redirectsRes.json());
      }
      
      await fetchMediaFiles();
      
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load settings');
      setSettingsLoaded(false);
      setLoading(false);
    }
  };

  const fetchMediaFiles = async () => {
    try {
      const mediaRes = await fetch(`${API_URL}/api/websites/${websiteId}/media`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (mediaRes.ok) {
        setMediaFiles(await mediaRes.json());
      }
    } catch {
      /* ignore — File Manager can retry on tab open */
    }
  };

  const uploadDataUrlIfNeeded = async (value, nameHint) => {
    if (!value || typeof value !== 'string') return value || '';
    if (!value.startsWith('data:image') || value.length < 400) return value;
    const blobRes = await fetch(value);
    const blob = await blobRes.blob();
    const ext = (blob.type || '').includes('jpeg') ? 'jpg' : 'png';
    const form = new FormData();
    form.append('file', blob, `${nameHint}.${ext}`);
    form.append('name', nameHint);
    const up = await fetch(`${API_URL}/api/websites/${websiteId}/media/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (!up.ok) {
      const err = await up.json().catch(() => ({}));
      throw new Error(typeof err.detail === 'string' ? err.detail : `Failed to upload ${nameHint}`);
    }
    const media = await up.json();
    const url = media.url || '';
    if (!url) throw new Error(`Upload returned no URL for ${nameHint}`);
    return url.startsWith('http') ? url : `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const handleSave = async () => {
    if (!settingsLoaded) {
      toast.error('Settings did not load — refresh before saving');
      return;
    }
    setSaving(true);
    try {
      // Persist oversized data-URL logos/favicons as media files so Save stays reliable
      // and the editor can load a normal URL.
      const logo_url = await uploadDataUrlIfNeeded(settings.logo_url, 'site-logo');
      const favicon_url = await uploadDataUrlIfNeeded(settings.favicon_url, 'site-favicon');
      const og_image_url = await uploadDataUrlIfNeeded(settings.og_image_url, 'site-og');

      const payload = {
        name: settings.name,
        description: settings.description,
        logo_url,
        favicon_url,
        og_image_url,
        seo_title: settings.seo_title,
        seo_description: settings.seo_description,
        seo_keywords: settings.seo_keywords,
        custom_head_code: settings.custom_head_code,
        custom_body_code: settings.custom_body_code,
        custom_css: settings.custom_css,
        cookie_consent_enabled: settings.cookie_consent_enabled,
        cookie_consent_message: settings.cookie_consent_message,
        privacy_policy_url: settings.privacy_policy_url,
        terms_url: settings.terms_url,
        social_facebook: settings.social_facebook,
        social_twitter: settings.social_twitter,
        social_instagram: settings.social_instagram,
        social_linkedin: settings.social_linkedin,
        social_youtube: settings.social_youtube,
        google_analytics_id: settings.google_analytics_id,
        facebook_pixel_id: settings.facebook_pixel_id,
        contact_email: settings.contact_email,
        from_email: settings.from_email,
        email_footer: settings.email_footer,
        chatbot_bot_id: settings.chatbot_bot_id,
        primary_color: settings.primary_color,
        secondary_color: settings.secondary_color,
        font_family: settings.font_family,
      };

      const response = await fetch(`${API_URL}/api/websites/${websiteId}/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const updated = await response.json().catch(() => ({}));
        setSettings((prev) => ({
          ...prev,
          ...payload,
          ...(updated || {}),
          logo_url: updated.logo_url || logo_url,
          favicon_url: updated.favicon_url || favicon_url,
          og_image_url: updated.og_image_url || og_image_url,
        }));
        await fetchMediaFiles();
        toast.success('Settings saved!');
      } else {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.detail === 'string' ? err.detail : 'Failed to save');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddRedirect = async () => {
    if (!newRedirect.from_path || !newRedirect.to_path) {
      toast.error('Both paths are required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/redirects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newRedirect)
      });
      
      if (response.ok) {
        const redirect = await response.json();
        setRedirects([...redirects, redirect]);
        setNewRedirect({ from_path: '', to_path: '', redirect_type: '301' });
        toast.success('Redirect added!');
      }
    } catch (error) {
      toast.error('Failed to add redirect');
    }
  };

  const handleDeleteRedirect = async (redirectId) => {
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/redirects/${redirectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRedirects(redirects.filter(r => r.id !== redirectId));
      toast.success('Redirect deleted');
    } catch (error) {
      toast.error('Failed to delete redirect');
    }
  };

  const handleAddMedia = async () => {
    if (!newMediaUrl) {
      toast.error('URL is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/websites/${websiteId}/media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newMediaName || 'Untitled',
          url: newMediaUrl
        })
      });
      
      if (response.ok) {
        const media = await response.json();
        setMediaFiles([...mediaFiles, media]);
        setNewMediaUrl('');
        setNewMediaName('');
        toast.success('File added!');
      }
    } catch (error) {
      toast.error('Failed to add file');
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    try {
      await fetch(`${API_URL}/api/websites/${websiteId}/media/${mediaId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setMediaFiles(mediaFiles.filter(m => m.id !== mediaId));
      toast.success('File deleted');
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };
  const handleUploadMedia = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const token = localStorage.getItem('token');
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('name', file.name);

        const res = await fetch(`${API_URL}/api/websites/${websiteId}/media/upload`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || 'Failed to upload file');
        }
        const media = await res.json();
        setMediaFiles((prev) => [...prev, media]);
      }
      toast.success('File(s) uploaded');
      event.target.value = '';
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to upload file');
    }
  };



  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const filteredMedia = mediaFilter === 'all' 
    ? mediaFiles 
    : mediaFiles.filter(m => m.file_type === mediaFilter);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">General Settings</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Website Title</label>
                  <input
                    type="text"
                    value={settings.name}
                    onChange={(e) => updateSetting('name', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                    placeholder="My Website"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={settings.description}
                    onChange={(e) => updateSetting('description', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                    rows={3}
                    placeholder="A brief description of your website"
                  />
                </div>

                <p className="text-xs text-zinc-500 rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2">
                  Custom domains and SSL are managed from the editor <strong className="text-zinc-300">Publish</strong> button.
                  Brand colors and fonts are under editor <strong className="text-zinc-300">Design</strong>.
                </p>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Logo URL</label>
                  <p className="text-xs text-zinc-400 mb-2">Header / brand logo (also set from AI Tools → Use in Website).</p>
                  <div className="flex gap-3 items-center">
                    {settings.logo_url && (
                      <img src={settings.logo_url} alt="Logo" className="w-14 h-14 rounded object-contain bg-zinc-900 border border-zinc-700" />
                    )}
                    <input
                      type="text"
                      value={
                        (settings.logo_url || '').startsWith('data:image')
                          ? '(embedded image — Save converts to a media URL)'
                          : (settings.logo_url || '')
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.startsWith('(embedded')) return;
                        updateSetting('logo_url', v);
                      }}
                      className="flex-1 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      placeholder="https://example.com/logo.png"
                      data-testid="settings-logo-url"
                    />
                  </div>
                  {(settings.logo_url || '').startsWith('data:image') && (
                    <p className="text-[11px] text-amber-400/90 mt-1">Large embedded logo detected. Click Save to store it as a normal media file for the editor.</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Favicon URL</label>
                  <p className="text-xs text-zinc-400 mb-2">Icon for your browser tab. Required dimensions 16x16 or 32x32 (.ico, .png)</p>
                  <div className="flex gap-3 items-center">
                    {settings.favicon_url && (
                      <img src={settings.favicon_url} alt="Favicon" className="w-10 h-10 rounded object-contain bg-zinc-900 border border-zinc-700" />
                    )}
                    <input
                      type="text"
                      value={
                        (settings.favicon_url || '').startsWith('data:image')
                          ? '(embedded image — Save converts to a media URL)'
                          : (settings.favicon_url || '')
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.startsWith('(embedded')) return;
                        updateSetting('favicon_url', v);
                      }}
                      className="flex-1 px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      placeholder="https://example.com/favicon.ico"
                    />
                  </div>
                  {(settings.favicon_url || '').startsWith('data:image') && (
                    <p className="text-[11px] text-amber-400/90 mt-1">Large embedded favicon detected. Click Save to store it as a normal media file.</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Social Sharing Image</label>
                  <p className="text-xs text-zinc-400 mb-2">Image to display on social networks when sharing a page.</p>
                  <div className="space-y-3">
                    {settings.og_image_url && (
                      <img src={settings.og_image_url} alt="Social" className="w-full max-w-md h-40 object-cover rounded-lg" />
                    )}
                    <input
                      type="text"
                      value={
                        (settings.og_image_url || '').startsWith('data:image')
                          ? '(embedded image — Save converts to a media URL)'
                          : (settings.og_image_url || '')
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.startsWith('(embedded')) return;
                        updateSetting('og_image_url', v);
                      }}
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                      placeholder="https://example.com/og-image.jpg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'seo':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">Page SEO</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">SEO Title</label>
                <input
                  type="text"
                  value={settings.seo_title}
                  onChange={(e) => updateSetting('seo_title', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="Page Title | Brand Name"
                />
                <p className="text-xs text-zinc-400 mt-1">{settings.seo_title?.length || 0}/60 characters</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Meta Description</label>
                <textarea
                  value={settings.seo_description}
                  onChange={(e) => updateSetting('seo_description', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                  rows={3}
                  placeholder="A compelling description for search engines..."
                />
                <p className="text-xs text-zinc-400 mt-1">{settings.seo_description?.length || 0}/160 characters</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Keywords</label>
                <input
                  type="text"
                  value={settings.seo_keywords}
                  onChange={(e) => updateSetting('seo_keywords', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>
              
              {/* Preview */}
              <div className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
                <p className="text-sm text-zinc-400 mb-2">Search Preview</p>
                <div className="bg-white p-4 rounded-lg text-black">
                  <div className="text-blue-600 text-lg hover:underline cursor-pointer">
                    {settings.seo_title || settings.name || 'Page Title'}
                  </div>
                  <div className="text-green-700 text-sm">
                    {website?.slug ? `yoursite.com/${website.slug}` : 'yoursite.com/page'}
                  </div>
                  <div className="text-gray-600 text-sm mt-1">
                    {settings.seo_description || 'Add a meta description to improve your search ranking.'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'custom-code':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">Custom Code</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Head Code</label>
                <p className="text-xs text-zinc-400 mb-2">Code inserted in the &lt;head&gt; section (analytics, fonts, etc.)</p>
                <textarea
                  value={settings.custom_head_code}
                  onChange={(e) => updateSetting('custom_head_code', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-green-400 font-mono text-sm resize-none"
                  rows={6}
                  placeholder="<!-- Google Analytics -->\n<script>...</script>"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Body Code</label>
                <p className="text-xs text-zinc-400 mb-2">Code inserted at the end of &lt;body&gt;</p>
                <textarea
                  value={settings.custom_body_code}
                  onChange={(e) => updateSetting('custom_body_code', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-green-400 font-mono text-sm resize-none"
                  rows={6}
                  placeholder="<!-- Chat widget -->\n<script>...</script>"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Custom CSS</label>
                <textarea
                  value={settings.custom_css}
                  onChange={(e) => updateSetting('custom_css', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg text-blue-400 font-mono text-sm resize-none"
                  rows={6}
                  placeholder=".custom-class {\n  color: #fff;\n}"
                />
              </div>
            </div>
          </div>
        );

      case 'media':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">File Manager</h3>
              <div className="flex gap-2">
                {['all', 'image', 'video', 'document'].map(type => (
                  <button
                    key={type}
                    onClick={() => setMediaFilter(type)}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      mediaFilter === type ? 'bg-violet-500' : 'bg-zinc-800'
                    }`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Add new media */}
            <div className="p-4 bg-zinc-800/50 rounded-lg border border-dashed border-zinc-700 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm text-zinc-300">Upload from your computer</p>
                <div className="flex items-center gap-2 text-xs text-zinc-500">
                  <span className="hidden md:inline">Max 10MB per file</span>
                </div>
              </div>
              <div className="flex flex-col md:flex-row gap-3 md:items-center">
                <input
                  type="file"
                  multiple
                  onChange={handleUploadMedia}
                  className="text-sm text-zinc-300 file:mr-4 file:px-3 file:py-2 file:rounded-lg file:border-0 file:bg-violet-600 file:text-white file:text-sm hover:file:bg-violet-500"
                />
              </div>
              <div className="flex gap-3 items-center pt-2 border-t border-zinc-700/60">
                <input
                  type="text"
                  value={newMediaName}
                  onChange={(e) => setNewMediaName(e.target.value)}
                  placeholder="File name"
                  className="w-40 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                />
                <input
                  type="url"
                  value={newMediaUrl}
                  onChange={(e) => setNewMediaUrl(e.target.value)}
                  placeholder="File URL (optional, for external files)"
                  className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                />
                <Button onClick={handleAddMedia} size="sm" className="bg-violet-500">
                  <Plus className="w-4 h-4 mr-1" />
                  Add URL
                </Button>
              </div>
            </div>
            
            {/* Media grid */}
            <div className="grid grid-cols-4 gap-4">
              {filteredMedia.map(file => (
                <div key={file.id} className="bg-zinc-800 rounded-lg overflow-hidden group relative">
                  {file.file_type === 'image' ? (
                    <img src={file.url} alt={file.name} className="w-full h-32 object-cover" />
                  ) : file.file_type === 'video' ? (
                    <div className="w-full h-32 bg-zinc-700 flex items-center justify-center">
                      <Video className="w-8 h-8 text-zinc-500" />
                    </div>
                  ) : (
                    <div className="w-full h-32 bg-zinc-700 flex items-center justify-center">
                      <FileText className="w-8 h-8 text-zinc-500" />
                    </div>
                  )}
                  <div className="p-2">
                    <p className="text-sm truncate">{file.name}</p>
                    <p className="text-xs text-zinc-400">{file.file_type}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteMedia(file.id)}
                    className="absolute top-2 right-2 p-1 bg-red-500 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            
            {filteredMedia.length === 0 && (
              <div className="text-center py-12 text-zinc-500">
                <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No files uploaded yet</p>
              </div>
            )}
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">Cookies & Privacy</h3>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="cookie"
                  checked={settings.cookie_consent_enabled}
                  onChange={(e) => updateSetting('cookie_consent_enabled', e.target.checked)}
                  className="w-5 h-5 rounded"
                />
                <label htmlFor="cookie" className="text-sm">Enable Cookie Consent Banner</label>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Cookie Message</label>
                <textarea
                  value={settings.cookie_consent_message}
                  onChange={(e) => updateSetting('cookie_consent_message', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Privacy Policy URL</label>
                <input
                  type="url"
                  value={settings.privacy_policy_url}
                  onChange={(e) => updateSetting('privacy_policy_url', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="/privacy-policy"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Terms of Service URL</label>
                <input
                  type="url"
                  value={settings.terms_url}
                  onChange={(e) => updateSetting('terms_url', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="/terms-of-service"
                />
              </div>
            </div>
          </div>
        );

      case 'integrations':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">Applications & Integrations</h3>

            <div className="p-4 bg-zinc-800/60 rounded-lg border border-zinc-700">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <span>Email &amp; SMS</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 uppercase tracking-wide">Notifications</span>
              </h4>
              <p className="text-xs text-zinc-400 mb-4">
                Configure providers for form notifications, booking confirmations, and campaigns. Full setup lives in Operate → Integrations.
              </p>
              {integrationsLoading ? (
                <p className="text-xs text-zinc-500">Loading integration status…</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-3 text-xs">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${integrationStatus.emailConfigured ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${integrationStatus.emailConfigured ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                      Email {integrationStatus.emailConfigured ? 'configured' : 'not configured'}
                    </span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${integrationStatus.smsConfigured ? 'bg-emerald-500/15 text-emerald-300' : 'bg-zinc-700 text-zinc-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${integrationStatus.smsConfigured ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                      SMS {integrationStatus.smsConfigured ? 'configured' : 'optional'}
                    </span>
                  </div>
                  {integrationStatus.fromEmail && (
                    <p className="text-[11px] text-zinc-500">
                      From: <span className="font-mono text-zinc-400">{integrationStatus.fromEmail}</span>
                      {' '}({integrationStatus.emailProvider})
                    </p>
                  )}
                  <p className="text-[11px] text-zinc-500">
                    Form alerts: inquiries {integrationStatus.inquiryNotifyOwner ? 'on' : 'off'}, leads {integrationStatus.leadNotifyOwner ? 'on' : 'off'}
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-zinc-700 text-xs"
                    onClick={() => window.open(`/sites/${websiteId}/operate?section=integrations`, '_blank')}
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-2" />
                    Open Operate Integrations
                  </Button>
                </div>
              )}
            </div>

            <div className="p-4 bg-zinc-800/60 rounded-lg border border-zinc-700">
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <span>Payment gateways</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 uppercase tracking-wide">Checkout</span>
              </h4>
              <p className="text-xs text-zinc-400 mb-4">
                Configure Stripe, Razorpay, or PayPal for storefront checkout. Also available under Operate → Integrations.
              </p>
              {paymentsLoading ? (
                <p className="text-xs text-zinc-500">Loading payment settings…</p>
              ) : (
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!paymentSettings.stripe_enabled}
                      onChange={(e) => updatePaymentSetting('stripe_enabled', e.target.checked)}
                    />
                    Enable Stripe
                  </label>
                  <input
                    type="text"
                    value={paymentSettings.stripe_publishable_key || ''}
                    onChange={(e) => updatePaymentSetting('stripe_publishable_key', e.target.value)}
                    placeholder="Stripe publishable key"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                  />
                  <input
                    type="password"
                    value={paymentSettings.stripe_secret_key || ''}
                    onChange={(e) => updatePaymentSetting('stripe_secret_key', e.target.value)}
                    placeholder="Stripe secret key (leave blank to keep)"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                  />
                  <input
                    type="password"
                    value={paymentSettings.stripe_webhook_secret || ''}
                    onChange={(e) => updatePaymentSetting('stripe_webhook_secret', e.target.value)}
                    placeholder="Stripe webhook secret (leave blank to keep)"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                  />
                  <p className="text-[11px] text-zinc-500 font-mono break-all">
                    Webhook URL: {API_URL}/payments/{websiteId}/stripe/webhook
                  </p>
                  <label className="flex items-center gap-2 text-sm mt-2">
                    <input
                      type="checkbox"
                      checked={!!paymentSettings.razorpay_enabled}
                      onChange={(e) => updatePaymentSetting('razorpay_enabled', e.target.checked)}
                    />
                    Enable Razorpay
                  </label>
                  <input
                    type="text"
                    value={paymentSettings.razorpay_key_id || ''}
                    onChange={(e) => updatePaymentSetting('razorpay_key_id', e.target.value)}
                    placeholder="Razorpay key ID"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                  />
                  <input
                    type="password"
                    value={paymentSettings.razorpay_key_secret || ''}
                    onChange={(e) => updatePaymentSetting('razorpay_key_secret', e.target.value)}
                    placeholder="Razorpay key secret (leave blank to keep)"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                  />
                  <label className="flex items-center gap-2 text-sm mt-2">
                    <input
                      type="checkbox"
                      checked={!!paymentSettings.paypal_enabled}
                      onChange={(e) => updatePaymentSetting('paypal_enabled', e.target.checked)}
                    />
                    Enable PayPal
                  </label>
                  <input
                    type="text"
                    value={paymentSettings.paypal_client_id || ''}
                    onChange={(e) => updatePaymentSetting('paypal_client_id', e.target.value)}
                    placeholder="PayPal client ID"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                  />
                  <input
                    type="password"
                    value={paymentSettings.paypal_client_secret || ''}
                    onChange={(e) => updatePaymentSetting('paypal_client_secret', e.target.value)}
                    placeholder="PayPal client secret (leave blank to keep)"
                    className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={paymentSettings.default_currency || 'USD'}
                      onChange={(e) => updatePaymentSetting('default_currency', e.target.value)}
                      className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                    >
                      <option value="USD">USD</option>
                      <option value="INR">INR</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                    <label className="flex items-center gap-2 text-sm px-2">
                      <input
                        type="checkbox"
                        checked={paymentSettings.test_mode !== false}
                        onChange={(e) => updatePaymentSetting('test_mode', e.target.checked)}
                      />
                      Sandbox / test mode
                    </label>
                  </div>
                  <Button type="button" size="sm" onClick={savePaymentSettings} disabled={paymentsSaving}>
                    {paymentsSaving ? 'Saving…' : 'Save payment gateways'}
                  </Button>
                </div>
              )}
            </div>
            
            <div className="space-y-4">
              <div className="p-4 bg-zinc-800/60 rounded-lg border border-zinc-700">
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <span>Website Chatbot</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 uppercase tracking-wide">AI Bot</span>
                </h4>
                <p className="text-xs text-zinc-400 mb-3">{BOT_HELP_TEXT}</p>

                {botsLoading ? (
                  <p className="text-xs text-zinc-500">Loading bots...</p>
                ) : bots.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex gap-2 items-center">
                      <select
                        value={settings.chatbot_bot_id || ''}
                        onChange={(e) => updateSetting('chatbot_bot_id', e.target.value)}
                        className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-white text-sm"
                      >
                        <option value="">Select a bot</option>
                        {bots.map((bot) => (
                          <option key={bot.id} value={bot.id}>
                            {bot.name || 'Untitled Bot'}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-zinc-700 text-xs"
                        onClick={() => window.open('/ai-studio', '_blank')}
                      >
                        Manage Bots
                      </Button>
                    </div>
                    {settings.chatbot_bot_id && (
                      <p className="mt-1 text-[11px] text-zinc-400 truncate">
                        Selected Bot ID: <span className="font-mono">{settings.chatbot_bot_id}</span>
                      </p>
                    )}
                    {settings.chatbot_bot_id && (
                      <p className="mt-1 text-[11px] text-emerald-400 flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        Chat widget will load on all published pages for this website.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {genreAgents?.templates?.length > 0 && (
                      <div className="rounded-lg border border-violet-500/30 bg-violet-950/30 p-3 mb-2">
                        <p className="text-xs text-zinc-300 mb-2">
                          Recommended for {genreAgents.icon} {genreAgents.label} sites:{' '}
                          <span className="text-white font-medium">
                            {(genreAgents.templates.find((t) => t.is_primary) || genreAgents.templates[0])?.name}
                          </span>
                        </p>
                        <Button
                          type="button"
                          size="sm"
                          className="bg-violet-600 hover:bg-violet-500 text-xs"
                          disabled={creatingGenreBot}
                          onClick={createGenreChatbot}
                        >
                          {creatingGenreBot ? 'Creating…' : 'Create & link industry chatbot'}
                        </Button>
                      </div>
                    )}
                    <p className="text-xs text-zinc-400">
                      No bots found yet. Use AI Studio to create an AI agent/bot, then return here to link it.
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="border-zinc-700 text-xs"
                      onClick={() => window.open('/ai-studio', '_blank')}
                    >
                      Open AI Studio
                    </Button>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Google Analytics ID</label>
                <input
                  type="text"
                  value={settings.google_analytics_id}
                  onChange={(e) => updateSetting('google_analytics_id', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="G-XXXXXXXXXX"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Facebook Pixel ID</label>
                <input
                  type="text"
                  value={settings.facebook_pixel_id}
                  onChange={(e) => updateSetting('facebook_pixel_id', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="123456789012345"
                />
              </div>
              
              <div className="border-t border-zinc-700 pt-4">
                <h4 className="font-medium mb-3">Social Media Links</h4>
                <div className="grid grid-cols-2 gap-4">
                  {['facebook', 'twitter', 'instagram', 'linkedin', 'youtube'].map(social => (
                    <div key={social}>
                      <label className="block text-sm font-medium mb-2 capitalize">{social}</label>
                      <input
                        type="url"
                        value={settings[`social_${social}`]}
                        onChange={(e) => updateSetting(`social_${social}`, e.target.value)}
                        className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                        placeholder={`https://${social}.com/yourpage`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 'auth':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-2">Visitor authentication</h3>
            <p className="text-sm text-zinc-400 mb-4">
              Let visitors create accounts on your published site. The Auth widget, Sign in page, and My Account dashboard
              all share the same login token key (<span className="font-mono text-zinc-300">site_token_</span> + your website id).
              Store checkout uses a separate customer account for orders.
            </p>

            <div className="grid gap-3 sm:grid-cols-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-zinc-700 justify-start"
                onClick={() => window.open(`/sites/${websiteId}/operate`, '_blank')}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-2" />
                Operate dashboard
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-zinc-700 justify-start"
                onClick={() => window.open(`${publishedSiteBase}?page=sign-in`, '_blank')}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-2" />
                Sign-in page
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-zinc-700 justify-start"
                onClick={() => window.open(`${publishedSiteBase}?page=my-account`, '_blank')}
              >
                <ExternalLink className="w-3.5 h-3.5 mr-2" />
                My Account page
              </Button>
            </div>

            <div className="p-4 bg-zinc-800/60 rounded-lg border border-zinc-700">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h4 className="text-sm font-semibold">Registered visitors</h4>
                <Button type="button" size="sm" variant="outline" className="border-zinc-700 text-xs" onClick={fetchSiteUsers}>
                  Refresh
                </Button>
              </div>
              {siteUsersLoading ? (
                <p className="text-xs text-zinc-500">Loading visitors…</p>
              ) : siteUsers.length === 0 ? (
                <p className="text-xs text-zinc-500">
                  No visitor accounts yet. Add an Auth widget in the editor or share your sign-in page link.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-zinc-400 border-b border-zinc-700">
                        <th className="py-2 pr-3 font-medium">Name</th>
                        <th className="py-2 pr-3 font-medium">Email</th>
                        <th className="py-2 pr-3 font-medium">Role</th>
                        <th className="py-2 pr-3 font-medium">Joined</th>
                        <th className="py-2 font-medium w-16" />
                      </tr>
                    </thead>
                    <tbody>
                      {siteUsers.map((visitor) => (
                        <tr key={visitor.id} className="border-b border-zinc-800/80">
                          <td className="py-2.5 pr-3 text-white">{visitor.name || '—'}</td>
                          <td className="py-2.5 pr-3 text-zinc-300">{visitor.email}</td>
                          <td className="py-2.5 pr-3 text-zinc-400 capitalize">{visitor.role || 'user'}</td>
                          <td className="py-2.5 pr-3 text-zinc-500 text-xs">
                            {visitor.created_at ? new Date(visitor.created_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="py-2.5 text-right">
                            <button
                              type="button"
                              onClick={() => deleteSiteUser(visitor.id)}
                              disabled={deletingSiteUserId === visitor.id}
                              className="text-red-400 hover:text-red-300 disabled:opacity-50"
                              title="Remove visitor"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[11px] text-zinc-500 mt-3">
                Manage email/SMS integrations and visitor lists in Operate → Integrations.
              </p>
            </div>
          </div>
        );

      case 'email':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">System Emails</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Contact Email</label>
                <input
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => updateSetting('contact_email', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="contact@yourcompany.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">From Email</label>
                <input
                  type="email"
                  value={settings.from_email}
                  onChange={(e) => updateSetting('from_email', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                  placeholder="noreply@yourcompany.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Email Footer</label>
                <textarea
                  value={settings.email_footer}
                  onChange={(e) => updateSetting('email_footer', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white resize-none"
                  rows={4}
                  placeholder="© 2025 Your Company. All rights reserved."
                />
              </div>
            </div>
          </div>
        );

      case 'redirects':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold mb-4">URL Redirects</h3>
            
            {/* Add new redirect */}
            <div className="p-4 bg-zinc-800/50 rounded-lg">
              <div className="grid grid-cols-4 gap-3">
                <input
                  type="text"
                  value={newRedirect.from_path}
                  onChange={(e) => setNewRedirect({ ...newRedirect, from_path: e.target.value })}
                  placeholder="/old-path"
                  className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                />
                <input
                  type="text"
                  value={newRedirect.to_path}
                  onChange={(e) => setNewRedirect({ ...newRedirect, to_path: e.target.value })}
                  placeholder="/new-path"
                  className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                />
                <select
                  value={newRedirect.redirect_type}
                  onChange={(e) => setNewRedirect({ ...newRedirect, redirect_type: e.target.value })}
                  className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                >
                  <option value="301">301 (Permanent)</option>
                  <option value="302">302 (Temporary)</option>
                </select>
                <Button onClick={handleAddRedirect} size="sm" className="bg-violet-500">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
            
            {/* Redirects list */}
            <div className="space-y-2">
              {redirects.map(redirect => (
                <div key={redirect.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-4">
                    <span className="text-zinc-400">{redirect.from_path}</span>
                    <span className="text-zinc-600">→</span>
                    <span>{redirect.to_path}</span>
                    <span className={`text-xs px-2 py-1 rounded ${
                      redirect.redirect_type === '301' ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'
                    }`}>
                      {redirect.redirect_type}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteRedirect(redirect.id)}
                    className="p-1 text-zinc-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              {redirects.length === 0 && (
                <div className="text-center py-12 text-zinc-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No redirects configured</p>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button onClick={() => navigate(`/edit/${websiteId}`)} variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Editor
          </Button>
          <div className="h-6 w-px bg-zinc-700" />
          <h1 className="text-lg font-semibold text-white">
            {website?.name} - Settings
          </h1>
        </div>
        
        <Button onClick={handleSave} disabled={saving || !settingsLoaded} className="bg-violet-500 hover:bg-violet-600">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Main Content */}
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-zinc-900 border-r border-white/10 min-h-[calc(100vh-73px)]">
          <div className="p-4 space-y-1">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-violet-500/20 text-violet-400' 
                      : 'text-zinc-400 hover:bg-zinc-800 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-sm">{tab.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-8 max-w-4xl">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default WebsiteSettings;
